import { ChangeDetectionStrategy, Component, computed, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EMPTY, from } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ApiService } from 'src/app/infrastructure/services/api-service/api.service';
import { DomainCreditsPanelComponent } from '../domain-credits-panel/domain-credits-panel.component';
import { creditPaymentDto, ICreditPaymentDto } from 'src/assets/common/models/credit-payment-dto.g';
import { domainCreditsDto, IDomainCreditsDto } from 'src/assets/common/models/domain-credits-dto.g';
import { domainCreditsListDto, IDomainCreditsListDto } from 'src/assets/common/models/domain-credits-list-dto.g';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';
import { money_format, number_format } from '../../models/credits-format';
import { unwrapData } from 'src/app/infrastructure/functions/unwrap-data.operator';

// Accountoverzicht in Instellingen (bijstelling 2026-10-26 (b), stap 3): de domains waarvan de gebruiker de
// klantrekening mag inzien (owner of gemachtigde), ook opgeschorte — die zijn via de domain-pagina niet bereikbaar.
// Een domain kiezen opent de klantrekening hier (zelfde component als de tab credits op het domain).
// Geen domains = de sectie blijft verborgen (hasDomains$$).
// Terugkeer van Mollie: ?payment={guid} → stand van de betaling ophalen (de backend boekt hem als hij betaald is),
// melding tonen, de klantrekening van dat domain openen en de parameter uit de url halen.
@Component({
    selector: 'app-account-credits',
    standalone: true,
    imports: [DomainCreditsPanelComponent],
    templateUrl: './account-credits.component.html',
    styleUrl: './account-credits.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountCreditsComponent {
    private readonly __apiService = injectRequired(ApiService);
    private readonly __route = injectRequired(ActivatedRoute);
    private readonly __router = injectRequired(Router);

    readonly domainCreditsListDto = domainCreditsListDto;
    readonly number_format = number_format;

    private readonly __domains$$ = signal<IDomainCreditsListDto[]>([]);
    readonly domains$$ = this.__domains$$.asReadonly();
    readonly hasDomains$$ = computed(() => this.domains$$().length > 0);
    private readonly __selected$$ = signal<string | null>(null);
    readonly selected$$ = this.__selected$$.asReadonly();
    private readonly __payment$$ = signal<ICreditPaymentDto | null>(null);
    readonly payment$$ = this.__payment$$.asReadonly();
    readonly paymentMessage$$ = computed(() => {
        const __p = this.payment$$();
        if (!__p) return null;
        const __what = `${number_format(__p[creditPaymentDto.credits])} credits voor ${__p[creditPaymentDto.domainName]} (${money_format(__p[creditPaymentDto.amount])})`;
        switch (__p[creditPaymentDto.status]) {
            case 'paid': return { ok: true, text: `Betaling ontvangen: ${__what} staan op het saldo. De factuur staat in het grootboek.` };
            case 'open':
            case 'pending':
            case 'authorized': return { ok: true, text: `Betaling van ${__what} is nog in behandeling. Zodra Mollie hem bevestigt, worden de credits bijgeboekt.` };
            case 'canceled': return { ok: false, text: `Betaling van ${__what} is geannuleerd; er is niets afgeschreven.` };
            case 'expired': return { ok: false, text: `Betaling van ${__what} is verlopen; er is niets afgeschreven.` };
            default: return { ok: false, text: `Betaling van ${__what} is mislukt; er is niets afgeschreven.` };
        }
    });

    protected readonly _text = {
        balance: 'saldo',
        owner: 'owner',
        readOnly: 'alleen-lezen',
        suspended: 'opgeschort',
        archived: 'gearchiveerd',
    };

    constructor() {
        from(this.__apiService.GET('/api/DomainCredits/list')).pipe(
            unwrapData(),
            catchError(() => EMPTY),
        ).subscribe(data => this.__domains$$.set(((data as any)?.list ?? []) as IDomainCreditsListDto[]));

        const __paymentGuid = this.__route.snapshot.queryParamMap.get('payment');
        if (__paymentGuid) this.__payment_load(__paymentGuid);
    }

    private __payment_load(paymentGuid: string): void {
        from(this.__apiService.GET('/api/DomainCredits/payment', { params: { query: { key: paymentGuid } } } as any)).pipe(
            unwrapData(),
            catchError(() => EMPTY),
        ).subscribe(data => {
            const __payment = ((data as any)?.modelDto ?? null) as ICreditPaymentDto | null;
            this.__payment$$.set(__payment);
            const __dauGuid = __payment?.[creditPaymentDto.domainAUserGuid];
            if (__dauGuid) this.__selected$$.set(__dauGuid);
        });
        this.__router.navigate([], { relativeTo: this.__route, queryParams: { payment: null }, queryParamsHandling: 'merge', replaceUrl: true });
    }

    emit_select(domain: IDomainCreditsListDto): void {
        const __dauGuid = domain[domainCreditsListDto.domainAUserGuid];
        this.__selected$$.set(this.selected$$() === __dauGuid ? null : __dauGuid);
    }

    // Wijziging in de klantrekening (archief): de rij in de lijst bijwerken.
    emit_changed(credits: IDomainCreditsDto): void {
        this.__domains$$.update(list => list.map(d => d[domainCreditsListDto.domainGuid] !== credits[domainCreditsDto.domainGuid] ? d : {
            ...d,
            [domainCreditsListDto.creditBalance]: credits[domainCreditsDto.creditBalance],
            [domainCreditsListDto.billingState]: credits[domainCreditsDto.billingState],
            [domainCreditsListDto.archived]: credits[domainCreditsDto.archived],
        }));
    }

    state_label(domain: IDomainCreditsListDto): string {
        if (domain[domainCreditsListDto.billingState] === 'suspended') return this._text.suspended;
        if (domain[domainCreditsListDto.archived]) return this._text.archived;
        if (domain[domainCreditsListDto.billingState] === 'readOnly') return this._text.readOnly;
        return '';
    }
}
